import { z } from "zod";

import { FinancialDocumentType } from "@/src/models/constants/document-types";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { locationSchema } from "@/src/models/location";
import { patientMinimalSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

export const financialTypeDisplay: Record<FinancialDocumentType, string> = {
  [FinancialDocumentType.ELIGIBILITY_OF_BENEFITS]: "Eligibility of Benefits",
  [FinancialDocumentType.BILLING_RECORDS]: "Billing Records PRS",
  [FinancialDocumentType.FINANCIAL]: "Financial",
  [FinancialDocumentType.ELIGIBILITY_OF_BENEFITS_CREDIT_CARD]: "Eligibility of Benefits/Credit Card",
  [FinancialDocumentType.LEGAL]: "Financial Legal Request",
};

const baseFinancialFields = {
  patient: patientMinimalSchema,
};

const extendedFinancialSchema = z.object({
  ...DocumentDateSchema.shape,
  location: locationSchema.nullable(),
  provider: practitionerSchema.nullable(),
  receivingProvider: practitionerSchema.nullable(),
  companyName: z.string().nullable(),
  documentType: z.string().nullable(),
  signatureRequired: z.boolean().nullable(),
});

const financialCustomFields: Record<FinancialDocumentType, Record<string, z.ZodTypeAny>> = {
  [FinancialDocumentType.BILLING_RECORDS]: extendedFinancialSchema.pick({ dateReceived: true }).shape,
  [FinancialDocumentType.ELIGIBILITY_OF_BENEFITS]: extendedFinancialSchema.pick({
    dateReceived: true,
    documentType: true,
    receivingProvider: true,
    signatureRequired: true,
  }).shape,
  [FinancialDocumentType.FINANCIAL]: extendedFinancialSchema.pick({
    dateReceived: true,
    documentType: true,
    receivingProvider: true,
    companyName: true,
    signatureRequired: true,
  }).shape,
  [FinancialDocumentType.ELIGIBILITY_OF_BENEFITS_CREDIT_CARD]: extendedFinancialSchema.pick({ dateReceived: true })
    .shape,
  [FinancialDocumentType.LEGAL]: extendedFinancialSchema.pick({ dateReceived: true }).shape,
};

const makeFinancialSchema = (type: FinancialDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...baseFinancialFields,
    ...customFields,
  });

export const financialSchemas: Record<FinancialDocumentType, z.ZodObject<any>> = (
  Object.values(FinancialDocumentType) as FinancialDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeFinancialSchema(type, financialCustomFields[type]);

    return acc;
  },
  {} as Record<FinancialDocumentType, z.ZodObject<any>>
);

const financialSchemasArray = (Object.values(FinancialDocumentType) as FinancialDocumentType[]).map(
  (type) => financialSchemas[type]
) as [z.ZodObject<any>, ...z.ZodObject<any>[]];

export const anyFinancialSchema = z.discriminatedUnion("type", financialSchemasArray);

export type Financial = z.infer<typeof anyFinancialSchema> & DatabaseDefaults;

const now = new Date().toISOString();

export const financialShape: Financial = {
  id: "",
  type: FinancialDocumentType.FINANCIAL,
  patient: null,
  dateReceived: null,
  receivingProvider: null,
  companyName: null,
  documentType: null,
  signatureRequired: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
